// A trip in the making, kept so it can be worked on for days or months (backend core/drafts).
// Signed in: saved to their account a moment after each change, every earlier version kept there.
// Not signed in: kept in this browser, so closing the tab loses nothing; it joins the account when they sign in.
import { useEffect, useRef } from "react";
import axios from "axios";
import { API } from "./constants";

export const DRAFT_STEPS = ["dates", "cities", "traveler", "style", "vibes", "practical", "input", "summary", "catalog"];
const LOCAL_KEY = "ju_localTrip";

export const tripStarted = (inputs) => !!(inputs?.dates?.startDate || (inputs?.selectedCities || []).length);

export function titleOf(inputs) {
  const cities = (inputs?.selectedCities || []).map(c => c?.name).filter(Boolean).slice(0, 3);
  const start = inputs?.dates?.startDate;
  const when = start ? new Date(`${start}T12:00:00`).toLocaleDateString("en-GB", { month: "short", day: "numeric" }) : "";
  const name = cities.length ? cities.join(" & ") : "My Japan trip";
  return when ? `${name} · ${when}` : name;
}

export function readLocalTrip() {
  try { return JSON.parse(localStorage.getItem(LOCAL_KEY) || "null"); } catch { return null; }
}

export function clearLocalTrip() {
  try { localStorage.removeItem(LOCAL_KEY); } catch { /* private mode */ }
}

// Saves the trip as they work on it. `inputs` and `selection` are small and sent on every change (debounced);
// the preview is large and only sent when it changed.
export function useTripDraft({ sessionToken, draftId, setDraftId, step, inputs, selection, preview }) {
  const lastSent = useRef({ state: "", preview: "" });
  const creating = useRef(false);

  useEffect(() => {
    if (!tripStarted(inputs)) return undefined;
    const atStep = DRAFT_STEPS.includes(step) ? step : undefined;
    // this browser keeps it either way
    try { localStorage.setItem(LOCAL_KEY, JSON.stringify({ inputs, step: atStep, savedAt: new Date().toISOString(), draftId })); } catch { /* quota */ }
    if (!sessionToken) return undefined;
    const state = JSON.stringify({ inputs, selection, step: atStep });
    const previewJson = JSON.stringify(preview || {});
    if (state === lastSent.current.state && previewJson === lastSent.current.preview) return undefined;
    const timer = setTimeout(async () => {
      const body = { inputs, selection, step: atStep };
      if (previewJson !== lastSent.current.preview && preview?.profilerBrief) body.preview = preview;
      const headers = { Authorization: `Bearer ${sessionToken}` };
      try {
        if (!draftId) {
          if (creating.current) return;
          creating.current = true;
          const res = await axios.post(`${API}/drafts`, body, { headers });
          setDraftId(res.data.draft_id);
        } else {
          await axios.put(`${API}/drafts/${draftId}`, body, { headers });
        }
        lastSent.current = { state, preview: body.preview ? previewJson : lastSent.current.preview };
      } catch (e) {
        if (e?.response?.status === 404 && draftId) setDraftId(null);     // removed elsewhere: the next change starts a new one
      } finally {
        creating.current = false;
      }
    }, 2500);
    return () => clearTimeout(timer);
  }, [sessionToken, draftId, setDraftId, step, inputs, selection, preview]);
}
